import { create } from 'zustand';
import {
  DashboardEntry,
  MenuChangeTemplate,
  RestaurantDetailResponse,
  RolloutDetail,
  fetchDashboard,
  fetchTemplates,
  fetchRestaurantDetail,
  fetchReminderStates,
  sendTaskReminder,
  TaskReminderState,
  MenuChangeRequestError,
} from '../services/menuChange/menuChangeService';

let requestVersion = 0;
let dashboardVersion = 0;
let sessionVersion = 0;
const detailVersions = new Map<string, number>();
const reminderVersions = new Map<string, number>();
const reminderRequests = new Map<string, { id: string; startedAt: number; previousSentAt: string | null }>();

interface MenuChangeStore {
  // All-restaurants dashboard (admin) — one entry per accessible restaurant.
  dashboard: DashboardEntry[];
  dashboardLoaded: boolean;
  isDashboardLoading: boolean;

  // Templates owned by the admin.
  templates: MenuChangeTemplate[];
  templatesLoaded: boolean;

  // Per-restaurant detail cache (active rollout + history + assignable users).
  detailByRestaurant: Record<string, RestaurantDetailResponse>;
  loadingDetail: Set<string>;
  detailErrors: Record<string, number>;
  remindersByRollout: Record<string, Record<string, TaskReminderState>>;
  loadingReminders: Set<string>;
  reminderErrors: Set<string>;
  sendingReminders: Set<string>;

  error: string | null;

  loadDashboard: (force?: boolean) => Promise<void>;
  loadTemplates: (force?: boolean) => Promise<void>;
  loadDetail: (restaurantId: string, force?: boolean) => Promise<RestaurantDetailResponse | undefined>;
  loadReminders: (rolloutId: string, force?: boolean) => Promise<void>;
  sendReminder: (rolloutId: string, taskId: string) => Promise<void>;
  // Replace a cached restaurant detail (e.g. after a task toggle returns fresh data).
  setDetail: (restaurantId: string, detail: RestaurantDetailResponse) => void;
  applyRollout: (restaurantId: string, rollout: RolloutDetail) => void;
  clear: () => void;
}

export const useMenuChangeStore = create<MenuChangeStore>((set, get) => ({
  dashboard: [],
  dashboardLoaded: false,
  isDashboardLoading: false,
  templates: [],
  templatesLoaded: false,
  detailByRestaurant: {},
  loadingDetail: new Set<string>(),
  detailErrors: {},
  remindersByRollout: {},
  loadingReminders: new Set<string>(),
  reminderErrors: new Set<string>(),
  sendingReminders: new Set<string>(),
  error: null,

  loadDashboard: async (force = false) => {
    const state = get();
    if (state.isDashboardLoading && !force) return;
    if (state.dashboardLoaded && !force) return;
    const version = ++requestVersion;
    dashboardVersion = version;
    set({ isDashboardLoading: true, error: null });
    try {
      const dashboard = await fetchDashboard();
      if (dashboardVersion !== version) return;
      set({ dashboard, dashboardLoaded: true, isDashboardLoading: false });
    } catch (error) {
      if (dashboardVersion !== version) return;
      console.error('Failed to load menu change dashboard:', error);
      set({
        isDashboardLoading: false,
        error: error instanceof Error ? error.message : 'Failed to load dashboard',
      });
    }
  },

  loadTemplates: async (force = false) => {
    if (get().templatesLoaded && !force) return;
    try {
      const templates = await fetchTemplates();
      set({ templates, templatesLoaded: true });
    } catch (error) {
      console.error('Failed to load menu change templates:', error);
      set({ error: error instanceof Error ? error.message : 'Failed to load templates' });
    }
  },

  loadDetail: async (restaurantId: string, force = false) => {
    const state = get();
    if (state.loadingDetail.has(restaurantId) && !force) return;
    if (state.detailByRestaurant[restaurantId] && !force) return state.detailByRestaurant[restaurantId];
    const version = ++requestVersion;
    detailVersions.set(restaurantId, version);
    set({ loadingDetail: new Set([...Array.from(state.loadingDetail), restaurantId]), detailErrors: { ...state.detailErrors, [restaurantId]: 0 } });
    try {
      const detail = await fetchRestaurantDetail(restaurantId);
      if (detailVersions.get(restaurantId) !== version) return;
      set((s) => ({ detailByRestaurant: { ...s.detailByRestaurant, [restaurantId]: detail } }));
      return detail;
    } catch (error) {
      if (detailVersions.get(restaurantId) !== version) return;
      console.error('Failed to load menu change detail:', error);
      set((s) => ({ error: error instanceof Error ? error.message : 'Failed to load detail', detailErrors: { ...s.detailErrors, [restaurantId]: error instanceof MenuChangeRequestError ? error.status : 500 } }));
    } finally {
      if (detailVersions.get(restaurantId) === version) {
        set((s) => ({ loadingDetail: new Set(Array.from(s.loadingDetail).filter((id) => id !== restaurantId)) }));
      }
    }
  },

  loadReminders: async (rolloutId, force = false) => {
    if (!force && (get().loadingReminders.has(rolloutId) || get().remindersByRollout[rolloutId])) return;
    const version = ++requestVersion;
    reminderVersions.set(rolloutId, version);
    set(s => ({ loadingReminders: new Set([...Array.from(s.loadingReminders), rolloutId]), reminderErrors: new Set(Array.from(s.reminderErrors).filter(id => id !== rolloutId)) }));
    try {
      const result = await fetchReminderStates(rolloutId);
      if (reminderVersions.get(rolloutId) !== version) return;
      for (const [taskId, reminder] of Object.entries(result.tasks)) {
        const key = `${rolloutId}:${taskId}`;
        const pending = reminderRequests.get(key);
        if (!pending || !reminder.lastSentAt) continue;
        // A recovered successful send resolves the uncertain action. After its
        // cooldown the next click must start a new action, not replay yesterday's ID.
        // Changed server history also covers a browser clock ahead of the server.
        if (Date.parse(reminder.lastSentAt) >= pending.startedAt ||
          (reminder.reason === 'cooldown' && reminder.lastSentAt !== pending.previousSentAt)) {
          reminderRequests.delete(key);
        }
      }
      set(s => ({ remindersByRollout: { ...s.remindersByRollout, [rolloutId]: result.tasks } }));
    } catch (error) {
      if (reminderVersions.get(rolloutId) !== version) return;
      console.error('Failed to load menu change reminders:', error);
      set(s => ({ reminderErrors: new Set([...Array.from(s.reminderErrors), rolloutId]) }));
    } finally {
      if (reminderVersions.get(rolloutId) === version) set(s => ({ loadingReminders: new Set(Array.from(s.loadingReminders).filter(id => id !== rolloutId)) }));
    }
  },

  sendReminder: async (rolloutId, taskId) => {
    const session = sessionVersion;
    const key = `${rolloutId}:${taskId}`;
    if (get().sendingReminders.has(key)) return;
    const action = reminderRequests.get(key) ?? {
      id: crypto.randomUUID(), startedAt: Date.now(), previousSentAt: get().remindersByRollout[rolloutId]?.[taskId]?.lastSentAt ?? null,
    };
    const requestId = action.id;
    reminderRequests.set(key, action);
    set(s => ({ sendingReminders: new Set([...Array.from(s.sendingReminders), key]) }));
    try {
      const result = await sendTaskReminder(rolloutId, taskId, requestId);
      if (session !== sessionVersion) return;
      reminderRequests.delete(key);
      reminderVersions.set(rolloutId, ++requestVersion);
      set(s => ({
        remindersByRollout: { ...s.remindersByRollout, [rolloutId]: { ...s.remindersByRollout[rolloutId], [taskId]: result.reminder } },
        loadingReminders: new Set(Array.from(s.loadingReminders).filter(id => id !== rolloutId)),
      }));
    } catch (error) {
      if (session !== sessionVersion) return;
      console.error('Failed to send menu change reminder:', error);
      // Keep the identity unless refreshed successful history resolves the action.
      await get().loadReminders(rolloutId, true);
      throw error;
    } finally {
      if (session === sessionVersion) set(s => ({ sendingReminders: new Set(Array.from(s.sendingReminders).filter(id => id !== key)) }));
    }
  },

  setDetail: (restaurantId, detail) => {
    detailVersions.set(restaurantId, ++requestVersion);
    set((s) => ({
      detailByRestaurant: { ...s.detailByRestaurant, [restaurantId]: detail },
      loadingDetail: new Set(Array.from(s.loadingDetail).filter((id) => id !== restaurantId)),
    }));
  },

  applyRollout: (restaurantId, rollout) => {
    detailVersions.set(restaurantId, ++requestVersion);
    set((s) => {
      const detail = s.detailByRestaurant[restaurantId];
      if (!detail) return {};
      return {
        dashboardLoaded: false,
        loadingDetail: new Set(Array.from(s.loadingDetail).filter((id) => id !== restaurantId)),
        detailByRestaurant: {
          ...s.detailByRestaurant,
          [restaurantId]: {
            ...detail,
            active: rollout.status === 'active' ? rollout : detail.active?.id === rollout.id ? undefined : detail.active,
            history: rollout.status === 'active'
              ? detail.history.filter((item) => item.id !== rollout.id)
              : detail.history.some((item) => item.id === rollout.id)
                ? detail.history.map((item) => item.id === rollout.id ? rollout : item)
                : [rollout, ...detail.history],
          },
        },
      };
    });
    void get().loadDashboard(true);
  },

  clear: () => {
    sessionVersion++;
    dashboardVersion = ++requestVersion;
    detailVersions.clear();
    reminderVersions.clear();
    reminderRequests.clear();
    set({
      dashboard: [],
      dashboardLoaded: false,
      isDashboardLoading: false,
      loadingDetail: new Set<string>(),
      templates: [],
      templatesLoaded: false,
      detailByRestaurant: {},
      detailErrors: {},
      remindersByRollout: {},
      loadingReminders: new Set<string>(),
      reminderErrors: new Set<string>(),
      sendingReminders: new Set<string>(),
      error: null,
    });
  },
}));
